"use client";

import type { JSONContent } from "@tiptap/react";

import type { ReactNode } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { RiSendPlaneLine } from "react-icons/ri";

import {
  CcBccToggle,
  ThreadComposerEditor,
} from "@/app/[tenant]/(console)/comms/chat/window/message/email-thread-dialog/thread-composer";

import {
  EmailRecipientField,
  useEmailAttachments,
  useEmailRecipients,
} from "@/components/email-composer";
import type { EmailComposerRef } from "@/components/email-composer";
import { trackEmailSentWithSenderConfig } from "@/components/email-composer/analytics";
import {
  mergeHeaderWithBody,
  wrapInRow,
} from "@/components/email-composer/converter/merge-header";
import { hydrateContent } from "@/components/email-composer/editor/resolve-mention";
import { EmailComposerSkeleton } from "@/components/email-composer/email-skeleton";
import { SenderFromField } from "@/components/email-composer/sender-from-field";
import type {
  EmailCellContent,
  EmailTopLevelNode,
} from "@/components/email-composer/types/tiptap-json";
import { ProgressBarLink } from "@/components/progress-bar";
import { Button, Input } from "@/components/ui";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";

import { useDefaultFromLocalPart } from "@/hooks/use-default-from-local-part";

import { toast } from "@/lib/use-toast";

import type { EmailTemplate } from "@/types/email-template";
import { EmailSenderNameMode } from "@/types/enums/email";

import { getSignedUrlBase64 } from "@/api/auth/gcs";
import {
  useComposeEmail,
  useEmailHeader,
  useEmailSignature,
} from "@/api/provider/comms/email/email-query";
import {
  getEmailSendErrorMessage,
  parseEmailSendError,
} from "@/api/provider/comms/email/error-message";
import { useSuggestions } from "@/api/query/provider/suggestions";

/** A document already stored in GCS to seed the composer's attachments with. */
export interface PatientEmailInitialAttachment {
  gcsPath: string;
  filename: string;
  fileType?: string;
  /**
   * Chart document this attachment came from. Sent alongside the file so the
   * email records which document left the building — the backend stores a fresh
   * copy of the bytes, so the path can never be matched back. Optional: an
   * ad-hoc attachment has no source document.
   */
  documentId?: string;
}

interface PatientEmailComposeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientEmail: string;
  patientId: string;
  /** Documents to fetch from GCS and pre-attach when the drawer opens. */
  initialAttachments?: PatientEmailInitialAttachment[];
  /** Already-built files (e.g. a client-generated PDF) to pre-attach on open. */
  initialFiles?: File[];
  /** Subject to open with. Editable, like a template's. */
  initialSubject?: string;
  /**
   * Body to open with, in place of the empty paragraphs. The clinic header and
   * the sender's signature still wrap it, so a caller supplies only its own
   * content — a greeting and a link button, say.
   *
   * Read once, when the drawer mounts: a caller that needs different content
   * must unmount the drawer between opens.
   */
  initialBodyNodes?: EmailTopLevelNode[];
  /**
   * Lock the message down for sending a patient's medical record: sender fixed
   * to the current provider, recipient fixed to the patient's email on file
   * (read-only), and no Cc/Bcc. Used by the document / completed-form sends.
   */
  restricted?: boolean;
}

/** Read-only From/To row shown in restricted (medical-record) mode. */
function LockedField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-border/50 py-2.5">
      <span className="text-body text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 truncate text-body text-foreground">
        {children}
      </span>
    </div>
  );
}

/**
 * In-place compose drawer for a patient's clickable email. Slides in over the
 * current page (the shared console layout portals it into the main content
 * column and runs it non-modal, so no navigation happens) and reuses the same
 * shared composer the email mailbox uses — `ThreadComposerEditor` plus the
 * recipient / sender field components.
 *
 * The send stays provider-scoped exactly like the old ComposeEmailModal it
 * replaces: linked to the patient via `patientId`, no `senderScope` (so the
 * backend keeps the individual provider as the From), and analytics source
 * "provider" — deliberately unlike the clinic mailbox pane, which sends with
 * `senderScope: "clinic"`.
 */
export default function PatientEmailComposeDrawer({
  open,
  onOpenChange,
  recipientEmail,
  patientId,
  initialAttachments,
  initialFiles,
  initialSubject,
  initialBodyNodes,
  restricted = false,
}: PatientEmailComposeDrawerProps) {
  const { tenant } = useTenant();
  const { currentUser } = useUser();
  const instanceId = useId();
  const composerRef = useRef<EmailComposerRef>(null);
  const [subject, setSubject] = useState(initialSubject ?? "");
  const [showCcBcc, setShowCcBcc] = useState(false);
  // Prefill the To field with the patient's email while keeping it editable so
  // the sender can add Cc/Bcc (the old dialog showed a read-only recipient).
  const toField = useEmailRecipients({ initialRecipients: [recipientEmail] });
  const ccField = useEmailRecipients();
  const bccField = useEmailRecipients();
  const defaultFromLocalPart = useDefaultFromLocalPart();
  const [editedFromLocalPart, setFromLocalPart] = useState<string | null>(null);
  const fromLocalPart = editedFromLocalPart ?? defaultFromLocalPart;
  const providerName = [currentUser?.firstName, currentUser?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  // "The person" — the individual provider. In restricted (medical-record) mode
  // the From is locked to the provider, not the clinic identity.
  const personSenderName = providerName || (tenant?.name ?? "");
  const defaultSenderName =
    tenant?.emailSenderNameMode === EmailSenderNameMode.CLINIC
      ? (tenant?.name ?? "")
      : personSenderName;
  const [senderName, setSenderName] = useState(
    restricted ? personSenderName : defaultSenderName,
  );
  const composeMutation = useComposeEmail();
  const {
    attachments,
    addFiles: addAttachmentFiles,
    removeFile: removeAttachment,
  } = useEmailAttachments();

  // Seed pre-attachments once per open. `initialFiles` are already-built File
  // objects (e.g. a client-generated PDF); `initialAttachments` live in GCS, so
  // the composer downloads each via a signed URL and wraps it in a File. Block
  // Send while any GCS fetch is in flight so the message can't go out without
  // its intended attachment.
  const [attachmentsLoading, setAttachmentsLoading] = useState(
    (initialAttachments?.length ?? 0) > 0,
  );
  const seededAttachments = useRef(false);
  // `File` can't carry the source document id, and the attachment list is
  // reorderable/removable, so index-based bookkeeping would drift. Key off the
  // File object itself instead: whatever survives to send time keeps its
  // provenance, and anything the user removed simply isn't looked up.
  const documentIdByFile = useRef(new WeakMap<File, string>());
  // Snapshot the seed payload until it's consumed. Both call sites pass fresh
  // array literals every render, so depending on them directly would re-run the
  // effect mid-fetch — its cleanup would cancel the in-flight GCS download,
  // dropping the attachment and leaving Send stuck disabled.
  const seedPayload = useRef({ initialAttachments, initialFiles });
  if (!seededAttachments.current) {
    seedPayload.current = { initialAttachments, initialFiles };
  }
  useEffect(() => {
    if (!open || seededAttachments.current) return;
    const { initialAttachments: gcsDocs, initialFiles: files } =
      seedPayload.current;
    const hasGcs = !!gcsDocs && gcsDocs.length > 0;
    const hasFiles = !!files && files.length > 0;
    if (!hasGcs && !hasFiles) {
      setAttachmentsLoading(false);
      return;
    }
    seededAttachments.current = true;
    let cancelled = false;
    (async () => {
      try {
        const gcsFiles = hasGcs
          ? await Promise.all(
              gcsDocs.map(async (att) => {
                // Fetch bytes via the backend base64 endpoint (server-side GCS
                // read) instead of fetching the signed URL directly — a browser
                // fetch of storage.googleapis.com is CORS-blocked. The response
                // is a data: URI, which fetches with no network hop.
                const response = await getSignedUrlBase64(att.gcsPath);
                if (!response?.signedUrl)
                  throw new Error("Failed to load document");
                const blob = await (await fetch(response.signedUrl)).blob();
                const file = new File([blob], att.filename, {
                  type: att.fileType || blob.type,
                });
                if (att.documentId)
                  documentIdByFile.current.set(file, att.documentId);
                return file;
              }),
            )
          : [];
        if (cancelled) return;
        addAttachmentFiles([...gcsFiles, ...(files ?? [])]);
      } catch {
        if (!cancelled) {
          toast({
            title: "Couldn't attach document",
            description:
              "The document couldn't be added. You can attach it manually.",
            variant: "error",
          });
        }
      } finally {
        if (!cancelled) setAttachmentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, addAttachmentFiles]);

  const { data: headerData, isLoading: isHeaderLoading } = useEmailHeader({
    enabled: open,
  });

  const signatureQuery = useEmailSignature();

  const { data: variables = [], isLoading: isSuggestionsLoading } =
    useSuggestions(
      ["PERSONAL", "TENANT", "SAVED", "EMAIL", "PATIENT"],
      patientId,
    );

  const variableMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const s of variables) {
      if (s.content) map[s.id] = s.content;
    }
    return map;
  }, [variables]);

  const isLoading =
    isHeaderLoading || signatureQuery.isLoading || isSuggestionsLoading;

  const initialContent = useMemo<JSONContent | undefined>(() => {
    if (isLoading) return undefined;

    // Build body: empty paragraphs for typing + signature
    const signatureContent = signatureQuery.data?.templateJson?.content;
    const signatureNodes =
      signatureContent && signatureContent.length > 0
        ? hydrateContent(signatureContent, variableMap)
        : [];

    const bodyNodes: EmailTopLevelNode[] = [
      ...(initialBodyNodes ?? [
        wrapInRow({ type: "emailParagraph", attrs: { textAlign: "left" } }),
        wrapInRow({ type: "emailParagraph", attrs: { textAlign: "left" } }),
      ]),
      ...signatureNodes.map((node) => wrapInRow(node as EmailCellContent)),
    ];

    return mergeHeaderWithBody(headerData?.templateJson, bodyNodes, {
      clinicName: variableMap["clinic_name"],
      logoUrl: variableMap["clinic_logo"],
    });
  }, [
    isLoading,
    headerData,
    signatureQuery.data,
    variableMap,
    initialBodyNodes,
  ]);

  // Gate "Send" on the tenant's verified transactional domain from the tenant
  // context (populated for every user), NOT on GET /admin/mailgun-domain — that
  // endpoint requires the EmailDomain-read (settings/admin) permission, so for a
  // non-admin provider it 403s, making the composer falsely believe no verified
  // domain exists and permanently disabling Send even though sending works.
  const hasTransactionalDomain =
    (tenant?.transactionalDomain?.state === "ACTIVE" &&
      tenant?.transactionalDomain?.isSendingVerified) ||
    tenant?.subDomain === "demo";

  // The picker lives inside ThreadComposerEditor and applies the body itself;
  // this only carries the subject across, which the drawer owns and gates Send
  // on.
  const handleLoadTemplate = (template: EmailTemplate) => {
    setSubject(template.subject);
  };

  const handleSend = async () => {
    if (!composerRef.current) return;

    const toRecipients = toField.getAll();
    if (toRecipients.length === 0) {
      toast({ title: "Please add a recipient", variant: "error" });
      return;
    }
    if (!subject.trim()) {
      toast({ title: "Please enter a subject", variant: "error" });
      return;
    }

    const [bodyHtml, bodyText] = await Promise.all([
      composerRef.current.getHtml(variableMap),
      composerRef.current.getText(variableMap),
    ]);

    const ccRecipients = ccField.getAll();
    const bccRecipients = bccField.getAll();

    const files = attachments.map((a) => a.file);
    const documentIds = files.map(
      (file) => documentIdByFile.current.get(file) ?? null,
    );

    composeMutation.mutate(
      {
        payload: {
          to: toRecipients,
          subject: subject.trim(),
          bodyHtml,
          bodyText,
          patientId,
          cc: ccRecipients.length > 0 ? ccRecipients : undefined,
          bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
          fromLocalPart: fromLocalPart.trim() || undefined,
          senderName: senderName.trim() || undefined,
        },
        files: files.length > 0 ? files : undefined,
        documentIds: documentIds.some(Boolean) ? documentIds : undefined,
      },
      {
        onSuccess: (response) => {
          if (!response?.success) {
            toast({
              title: "Failed to send email",
              description: getEmailSendErrorMessage(
                response?.error,
                "The email was not sent. Please try again.",
              ),
              variant: "error",
            });
            return;
          }

          trackEmailSentWithSenderConfig(
            "compose",
            senderName,
            defaultSenderName,
            fromLocalPart,
            defaultFromLocalPart,
            "provider",
          );
          toast({ title: "Email sent", variant: "success" });
          onOpenChange(false);
        },
        onError: async (error) => {
          toast({
            title: "Failed to send email",
            description: await parseEmailSendError(
              error,
              "The email was not sent. Please try again.",
            ),
            variant: "error",
          });
        },
      },
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="!w-full !max-w-2xl">
        <DrawerHeader>
          <DrawerTitle>Compose email</DrawerTitle>
        </DrawerHeader>
        {/*
          px-4 is load-bearing, not cosmetic: the editor's column-resize handles
          (.email-cell-resize-handle) are 6px wide and centered on each cell
          boundary, so the last one overhangs the content's right edge by 3px.
          With no horizontal padding that 3px overflows this scroll container
          (overflow-y-auto implies overflow-x auto), scrolling the whole drawer
          body sideways and clipping the From/To/Subject labels. The mailbox
          compose pane absorbs the same overhang with its own px-4.
        */}
        <DrawerBody className="px-4">
          <div>
            {restricted ? (
              <>
                {/* Locked identity for a medical-record send: fixed provider
                    sender, fixed patient recipient, no Cc/Bcc. */}
                <LockedField label="From">
                  {senderName}
                  {tenant?.transactionalDomain?.domainName ? (
                    <span>{` <${fromLocalPart || tenant?.subDomain}@${tenant.transactionalDomain.domainName}>`}</span>
                  ) : (
                    <span>{""}</span>
                  )}
                </LockedField>
                <LockedField label="To">{recipientEmail}</LockedField>
              </>
            ) : (
              <>
                <div className="border-b border-border/50 py-1.5">
                  <SenderFromField
                    name={senderName}
                    onNameChange={setSenderName}
                    localPart={fromLocalPart}
                    onLocalPartChange={setFromLocalPart}
                    domain={tenant?.transactionalDomain?.domainName ?? ""}
                    placeholder={tenant?.subDomain ?? ""}
                  />
                </div>
                <div className="flex items-center gap-2 border-b border-border/50">
                  <EmailRecipientField
                    label="To"
                    field={toField}
                    placeholder="Add recipients..."
                    flat
                    className="!space-y-0 min-w-0 flex-1 py-1.5"
                  />
                  {!showCcBcc && (
                    <CcBccToggle onClick={() => setShowCcBcc(true)} />
                  )}
                </div>
                {showCcBcc && (
                  <>
                    <div className="border-b border-border/50">
                      <EmailRecipientField
                        label="Cc"
                        field={ccField}
                        placeholder="Add Cc recipients..."
                        flat
                        className="!space-y-0 py-1.5"
                      />
                    </div>
                    <div className="border-b border-border/50">
                      <EmailRecipientField
                        label="Bcc"
                        field={bccField}
                        placeholder="Add Bcc recipients..."
                        flat
                        className="!space-y-0 py-1.5"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div className="border-b border-border/50">
              <Input
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="Subject"
                className="!rounded-none !border-0 bg-transparent px-0 py-2.5 text-body !shadow-none !ring-0"
              />
            </div>
            {isLoading ? (
              <div className="pt-3">
                <EmailComposerSkeleton />
              </div>
            ) : (
              <ThreadComposerEditor
                composerRef={composerRef}
                composerId={`compose:${patientId}:${instanceId}`}
                initialContent={initialContent}
                variables={variables}
                attachments={attachments}
                onAddFiles={addAttachmentFiles}
                onRemoveAttachment={removeAttachment}
                placeholder="Compose your email... Use @ to insert dynamic values."
                canvasClassName="min-h-40"
                className="pt-3"
                onTemplateSelect={handleLoadTemplate}
                autoFocus
              />
            )}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!hasTransactionalDomain ? (
            <Button
              icon={<RiSendPlaneLine />}
              disabled
              tooltip={
                <span>
                  A verified transactional email domain is required to send
                  emails. Configure one in{" "}
                  <ProgressBarLink href={`/settings/emails`}>
                    Settings &gt; Emails
                  </ProgressBarLink>
                  .
                </span>
              }
            >
              Send
            </Button>
          ) : (
            <Button
              onClick={handleSend}
              icon={<RiSendPlaneLine />}
              isLoading={composeMutation.isPending}
              loadingText="Sending..."
              disabled={isLoading || attachmentsLoading}
            >
              Send
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
